import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function AuthConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const confirm = async () => {
      const tokenHash = searchParams.get("token_hash");
      const type = searchParams.get("type") as
        | "signup"
        | "magiclink"
        | "recovery"
        | "email_change"
        | null;
      const redirectTo = searchParams.get("redirect_to");

      if (!tokenHash || !type) {
        setStatus("error");
        setMessage("Invalid confirmation link. Missing token or type.");
        return;
      }

      try {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });

        if (error) throw error;

        setStatus("success");

        switch (type) {
          case "signup":
            setMessage("Your email has been verified! Redirecting...");
            break;
          case "recovery":
            setMessage("Password reset verified. Redirecting...");
            break;
          case "email_change":
            setMessage("Email change confirmed. Redirecting...");
            break;
          default:
            setMessage("Confirmed! Redirecting...");
        }

        setTimeout(() => {
          if (redirectTo) {
            // If redirect_to is a full URL, extract the path
            try {
              const url = new URL(redirectTo);
              navigate(url.pathname + url.search);
            } catch {
              navigate(redirectTo);
            }
          } else if (type === "recovery") {
            navigate("/auth/reset-password");
          } else {
            navigate("/crew-login?verified=true");
          }
        }, 2000);
      } catch (error: unknown) {
        console.error(error);
        setStatus("error");
        setMessage(
          error instanceof Error ? error.message : "Failed to confirm. The link may have expired.",
        );
      }
    };

    confirm();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
            <h2 className="mb-2 text-2xl font-bold">Confirming...</h2>
            <p className="text-muted-foreground">Please wait while we verify your request.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
            <h2 className="mb-2 text-2xl font-bold">Confirmed!</h2>
            <p className="mb-4 text-muted-foreground">{message}</p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
            <h2 className="mb-2 text-2xl font-bold">Confirmation Failed</h2>
            <p className="mb-6 text-muted-foreground">{message}</p>
            <Button onClick={() => navigate("/crew-login")} className="w-full">
              Get the crew app
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
